import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import * as echarts from 'echarts';
import { utils, writeFile } from 'xlsx';
import { Box, Button, Typography, useTheme } from '@mui/material';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChartSeries {
  id?: string;
  name: string;
  data: [number | string, number | null][];
  lineStyle?: { type?: 'solid' | 'dashed' | 'dotted'; width?: number };
  color?: string;
}

interface EChartsStockChartProps {
  title?: string;
  series?: ChartSeries[];
  yAxisTitle?: string;
  prefix?: string;
  suffix?: string;
  locale?: string;
  height?: number | string;
  hideSlider?: boolean;
  hideZoomButtons?: boolean;
  simplifiedXAxis?: boolean;
  rebasable?: boolean;
}


// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (ts: number, simplified = false): string => {
  if (!ts || isNaN(ts)) return '--/--/----';
  const d = new Date(ts);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return simplified ? `${mm}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
};

function normalizeTime(t: number | string): number | null {
  if (typeof t === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const [y, m, d] = t.split('-').map(Number);
    const result = Date.UTC(y, m - 1, d);
    return isNaN(result) ? null : result;
  }
  const dt = new Date(t);
  dt.setUTCHours(0, 0, 0, 0);
  const result = dt.getTime();
  return isNaN(result) ? null : result;
}

const isEqual = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  return keys.every(k => a[k] === b[k]);
};

const RANGE_BUTTONS = [
  { label: '1m', calc: (max: number, min: number) => { const d = new Date(max); d.setUTCMonth(d.getUTCMonth() - 1); return Math.max(d.getTime(), min); } },
  { label: '3m', calc: (max: number, min: number) => { const d = new Date(max); d.setUTCMonth(d.getUTCMonth() - 3); return Math.max(d.getTime(), min); } },
  { label: '6m', calc: (max: number, min: number) => { const d = new Date(max); d.setUTCMonth(d.getUTCMonth() - 6); return Math.max(d.getTime(), min); } },
  { label: 'YTD', calc: (max: number, min: number) => { const d = new Date(max); return Math.max(Date.UTC(d.getUTCFullYear(), 0, 1), min); } },
  { label: '1y', calc: (max: number, min: number) => { const d = new Date(max); d.setUTCFullYear(d.getUTCFullYear() - 1); return Math.max(d.getTime(), min); } },
  { label: 'All', calc: (_max: number, min: number) => min },
];

// ─── Component ───────────────────────────────────────────────────────────────

const EChartsStockChart: React.FC<EChartsStockChartProps> = React.memo(({
  title,
  series = [],
  yAxisTitle,
  prefix = '',
  suffix = '',
  locale = 'pt-BR',
  height = 600,
  hideSlider = false,
  hideZoomButtons = false,
  simplifiedXAxis = false,
  rebasable = false,
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const initialMinRef = useRef(0);
  const initialMaxRef = useRef(0);
  const isRangeInitialized = useRef(false);

  const [range, setRange] = useState({ start: 0, end: 0 });
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({});
  const [activeRange, setActiveRange] = useState<string | null>(null);
  const [rebaseFrom, setRebaseFrom] = useState<number | null>(null);
  const rebaseFromRef = useRef<number | null>(null);

  const setZoom = useCallback((newStart: number, newEnd: number) => {
    if (chartInstance.current) {
      chartInstance.current.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: [0, 1],
        startValue: newStart,
        endValue: newEnd,
      });
      setRange({ start: newStart, end: newEnd });
      isRangeInitialized.current = true;
    }
  }, []);

  // Rebase: recalcula acumulado a partir do início do período selecionado
  const effectiveSeries: ChartSeries[] = useMemo(() => {
    if (!rebasable || rebaseFrom == null || !series?.length) return series;
    return series.map(s => {
      const normalized = s.data.map(([t, v]) => [
        typeof t === 'number' ? t : (normalizeTime(t as string) ?? t),
        v,
      ] as [number | string, number | null]);
      const baseIdx = normalized.findIndex(([t]) => (t as number) >= rebaseFrom);
      if (baseIdx < 0) return s;
      const baseVal = normalized[baseIdx][1];
      if (baseVal == null) return { ...s, data: normalized };
      const base = 1 + (baseVal as number) / 100;
      return {
        ...s,
        data: normalized.map(([t, v]) => [
          t,
          v == null ? null : ((1 + (v as number) / 100) / base - 1) * 100,
        ]) as [number | string, number | null][],
      };
    });
  }, [series, rebaseFrom, rebasable]);

  const exportToExcel = useCallback(() => {
    if (!series.length) return;
    const ds = series.map(s => ({
      name: s.name,
      data: s.data
        .map(([t, v]) => [normalizeTime(t), (typeof v === 'number' && isFinite(v)) ? v : null] as [number | null, number | null])
        .filter(([t]) => t != null),
    }));
    const allTs = Array.from(new Set(ds.flatMap(s => s.data.map(([t]) => t)))).sort((a, b) => (a ?? 0) - (b ?? 0));
    const rows = allTs.map(t => {
      const row: Record<string, string | number | null> = { Date: formatDate(t ?? 0) };
      ds.forEach(s => {
        const pt = s.data.find(([tt]) => tt === t);
        row[s.name] = pt ? pt[1] : null;
      });
      return row;
    });
    const ws = utils.json_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Dados');
    writeFile(wb, `${title ?? 'chart'}_data.xlsx`);
  }, [series, title]);

  // 1) Init ECharts instance once
  useEffect(() => {
    if (!chartRef.current || chartInstance.current) return;
    const chart = echarts.init(chartRef.current);
    chartInstance.current = chart;

    const onDataZoom = () => {
      const opt = chart.getOption() as any;
      const dz: any[] = opt.dataZoom ?? [];
      const slider = dz.find((d: any) => d.type === 'slider');
      const inside = dz.find((d: any) => d.type === 'inside');
      const start = slider?.startValue ?? inside?.startValue ?? 0;
      const end = slider?.endValue ?? inside?.endValue ?? 0;
      setRange({ start, end });
    };

    chart.on('datazoom', onDataZoom);
    chart.on('legendselectchanged', (e: any) => {
      setLegendSelected(prev => ({ ...prev, ...e.selected }));
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      chart.off('datazoom', onDataZoom);
      chart.off('legendselectchanged');
      window.removeEventListener('resize', handleResize);
      chart.dispose();
      chartInstance.current = null;
    };
  }, []);

  // 2) Fixed options (theme/layout)
  useEffect(() => {
    if (!chartInstance.current) return;
    const textColor = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const dividerColor = theme.palette.divider;
    const isDark = theme.palette.mode === 'dark';

    chartInstance.current.setOption({
      title: { text: title, left: 'center', textStyle: { color: textColor } },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
        borderColor: dividerColor,
        textStyle: { color: textColor },
        formatter: (params: any[]) => {
          const valid = params.filter(p => p.data && p.data[1] != null);
          if (!valid.length) return '';
          const date = formatDate(valid[0].data[0], simplifiedXAxis);
          return valid.reduce((html, p) => {
            const fv = new Intl.NumberFormat(locale, { style: 'decimal', maximumFractionDigits: 4 }).format(p.data[1]);
            return html + `<span style="color:${p.color}">●</span> ${p.seriesName}: <strong>${prefix}${fv}${suffix}</strong><br/>`;
          }, `<strong>${date}</strong><br/>`);
        },
      },
      legend: hideSlider
        ? { show: false }
        : {
            type: 'scroll', bottom: 0,
            textStyle: { color: textSecondary },
            pageIconColor: textSecondary,
            pageIconInactiveColor: dividerColor,
            pageTextStyle: { color: textSecondary },
            formatter: (name: string) => name.length > 40 ? name.slice(0, 37) + '...' : name,
            tooltip: {
              show: true,
              formatter: (p: any) => p.name,
              backgroundColor: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(0,0,0,0.85)',
              borderColor: 'transparent',
              textStyle: { color: isDark ? '#E0E0E0' : '#fff', fontSize: 13 },
              padding: [8, 12],
            },
          },
      toolbox: {
        iconStyle: { borderColor: textSecondary },
        feature: {
          saveAsImage: { title: 'Exportar imagem' },
          dataZoom: { yAxisIndex: false, title: { zoom: 'Zoom', back: 'Reset' } },
          myExport: { show: true, title: 'Exportar XLSX', icon: 'path://M512 64L512 640M384 512L512 640L640 512', onclick: exportToExcel },
        },
        right: 20,
      },
      dataZoom: [
        { type: 'inside', xAxisIndex: 0 },
        ...(!hideSlider ? [{
          type: 'slider', xAxisIndex: 0, bottom: 30, height: 20,
          labelFormatter: (val: number) => formatDate(val, simplifiedXAxis),
          textStyle: { color: textSecondary },
          borderColor: dividerColor,
          dataBackground: { lineStyle: { color: dividerColor }, areaStyle: { color: dividerColor } },
        }] : []),
      ],
      xAxis: {
        type: 'time',
        minInterval: 24 * 3600 * 1000,
        axisLabel: { formatter: (val: number) => formatDate(val, simplifiedXAxis), hideOverlap: true, color: textSecondary },
        axisLine: { lineStyle: { color: dividerColor } },
        axisPointer: { label: { formatter: (p: any) => formatDate(p.value, simplifiedXAxis) } },
      },
      yAxis: {
        type: 'value',
        name: yAxisTitle,
        nameTextStyle: { color: textSecondary },
        axisLabel: { color: textSecondary },
        axisLine: { lineStyle: { color: dividerColor } },
        splitLine: { lineStyle: { type: 'dashed', color: dividerColor } },
      },
      grid: { top: 90, left: 50, right: 30, bottom: hideSlider ? 40 : 100, containLabel: true },
      useUTC: true,
    }, { notMerge: true, lazyUpdate: true });
  }, [title, yAxisTitle, prefix, suffix, locale, exportToExcel, hideSlider, simplifiedXAxis, theme]);

  // 3) Series update
  useEffect(() => {
    if (!chartInstance.current || !effectiveSeries?.length) return;

    const displaySeries = effectiveSeries.map(s => ({
      id: s.id ?? s.name,
      name: s.name,
      type: 'line',
      showSymbol: false,
      smooth: false,
      emphasis: { focus: 'series' },
      animationDuration: 500,
      animationEasing: 'cubicOut',
      ...(s.lineStyle ? { lineStyle: s.lineStyle } : {}),
      ...(s.color ? { itemStyle: { color: s.color }, lineStyle: { ...(s.lineStyle ?? {}), color: s.color } } : {}),
      data: s.data
        .map(([t, v]) => [normalizeTime(t), (typeof v === 'number' && isFinite(v)) ? v : null])
        .filter(([t]) => t != null),
    }));

    const presentNames = new Set(displaySeries.map(s => s.name));
    const allTs = Array.from(new Set(displaySeries.flatMap(s => s.data.map(([t]) => t as number)))).sort((a, b) => a - b);
    const min = allTs[0];
    const max = allTs[allTs.length - 1];
    initialMinRef.current = min ?? 0;
    initialMaxRef.current = max ?? 0;

    const prunedSelected = Object.fromEntries(Object.entries(legendSelected).filter(([name]) => presentNames.has(name)));
    const baseSelected = Object.keys(prunedSelected).length
      ? prunedSelected
      : Object.fromEntries(displaySeries.map(s => [s.name, true]));
    const mergedSelected: Record<string, boolean> = { ...baseSelected };
    for (const s of displaySeries) {
      if (!(s.name in mergedSelected)) mergedSelected[s.name] = true;
    }

    chartInstance.current.setOption(
      { legend: { selected: mergedSelected }, xAxis: { min: min ?? null, max: max ?? null }, series: displaySeries },
      { notMerge: false, replaceMerge: ['series'], lazyUpdate: true },
    );

    if (min && max && !isRangeInitialized.current) {
      setRange({ start: min, end: max });
      isRangeInitialized.current = true;
    }

    // Após atualizar séries com rebase, reaplicar o zoom para o período selecionado
    const rFrom = rebaseFromRef.current;
    if (rFrom != null && max) {
      requestAnimationFrame(() => {
        chartInstance.current?.dispatchAction({
          type: 'dataZoom',
          dataZoomIndex: [0, 1],
          startValue: rFrom,
          endValue: initialMaxRef.current,
        });
      });
    }

    if (!isEqual(mergedSelected as Record<string, unknown>, legendSelected as Record<string, unknown>)) setLegendSelected(mergedSelected);
  }, [effectiveSeries, legendSelected]);

  if (!series?.length) {
    return (
      <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 2, boxShadow: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
        height: typeof height === 'number' ? `${height}px` : height }}>
        <Typography color="text.secondary">Sem dados para exibir</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'background.paper', p: 1.5, borderRadius: 1.5,
      border: '1px solid', borderColor: 'divider',
      display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%',
      height: typeof height === 'number' ? `${height}px` : height }}>
      {!hideZoomButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {RANGE_BUTTONS.map(btn => {
              const isActive = activeRange === btn.label;
              return (
                <Button key={btn.label} variant={isActive ? 'contained' : 'outlined'} size="small"
                  onClick={() => {
                    const newStart = btn.calc(initialMaxRef.current, initialMinRef.current);
                    setZoom(newStart, initialMaxRef.current);
                    setActiveRange(btn.label);
                    if (rebasable) {
                      const nextRebase = btn.label === 'All' ? null : newStart;
                      rebaseFromRef.current = nextRebase;
                      setRebaseFrom(nextRebase);
                    }
                  }}
                  sx={{
                    minWidth: 0, px: 1.2, py: 0.3, fontSize: '11px', lineHeight: 1.4,
                    borderColor: isActive ? 'primary.main' : 'divider',
                    color: isActive ? '#fff' : 'text.secondary',
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    '&:hover': { borderColor: 'primary.main', color: isActive ? '#fff' : 'primary.main' },
                  }}>
                  {btn.label}
                </Button>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            {rebasable && rebaseFrom != null
              ? `↩ rebase: ${formatDate(rebaseFrom, simplifiedXAxis)} → ${range.end ? formatDate(range.end, simplifiedXAxis) : '--/--/----'}`
              : `${range.start ? formatDate(range.start, simplifiedXAxis) : '--/--/----'} → ${range.end ? formatDate(range.end, simplifiedXAxis) : '--/--/----'}`
            }
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Box ref={chartRef} sx={{ width: '100%', height: '100%', minWidth: 0 }} role="img" aria-label={title ?? 'Gráfico'} />
      </Box>
    </Box>
  );
});

export default EChartsStockChart;
